// import components
import {
  Checkbox,
  ClickAwayListener,
  MenuItem,
  Paper,
  Popper
} from '@mui/material';
import { StyledPaperBox } from 'components/shared/ToolbarSet/components/VerticalToolbarActionWrapper.styles';

// import external libraries
import { useTranslation } from 'react-i18next';

// import hooks
import { useIsVerticalToolbar } from 'components/shared/ToolbarSet/ToolbarPosition.context';

// import styles
import {
  BacklogCheckboxRow,
  BacklogControlStack,
  BacklogFieldContainer,
  BacklogToolbarField,
  ViewSettingCheckboxLabel
} from '../../Backlog.styles';

// import types
import type { BacklogCellValueMode } from '../../Backlog.types';
import type { BacklogViewSettingsProps } from './BacklogViewSettings.types';

/**
 * Everything that changes *how* the same tasks are drawn, as opposed to which ones are listed: what the
 * cells read, and whether fulfilled weeks and the totals column are shown.
 *
 * Only the popover lives here — its eye icon is a right-aligned toolbar action, sitting beside the
 * toolbar's own overflow menu rather than with the filters on the left, so the page owns the open state
 * and this anchors itself under that icon by id. It is still rendered from a toolbar slot, which is what
 * tells it whether the toolbar is docked to the side.
 */
const BacklogViewSettings = ({
  cellValueMode,
  onCellValueModeChange,
  showTotals,
  onShowTotalsChange,
  showCompleted,
  onShowCompletedChange,
  isOpen,
  onClose,
  anchorId
}: BacklogViewSettingsProps) => {
  'use memo';

  // Popper computes placement at runtime, so it cannot come from CSS — but the layout is the
  // toolbar's, so it is read from the toolbar rather than handed over as a prop.
  const isVerticalToolbar = useIsVerticalToolbar();

  const { t } = useTranslation('common', { keyPrefix: 'backlog' });

  const cellValueOptions = [
    { value: 'difference', label: t('cellContent.difference') },
    {
      value: 'plannedOverPrescribed',
      label: t('cellContent.plannedOverPrescribed')
    }
  ];

  /**
   * The same borderless toolbar field the two filters render, so all three dropdowns match: the plan unit
   * selector's StyledCustomTextField with isToolbar, its label floating above the value. In select mode
   * rather than an autocomplete, since this is one choice out of two fixed options with nothing to
   * search.
   *
   * Not components/shared/Select, which passes htmlFor={label} — the label text, not an element id — so
   * its field ends up with no accessible name at all.
   */
  const cellValueSelect = (
    <BacklogFieldContainer>
      <BacklogToolbarField
        select
        label={t('cellContent.label')}
        isToolbar
        variant="standard"
        size="small"
        fullWidth
        slotProps={{ inputLabel: { shrink: true } }}
        value={cellValueMode}
        onChange={(event) =>
          onCellValueModeChange(event.target.value as BacklogCellValueMode)
        }
      >
        {cellValueOptions.map(({ value, label }) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </BacklogToolbarField>
    </BacklogFieldContainer>
  );

  /**
   * One helper for both checkboxes, so the pair cannot drift apart: the same compact checkbox with its
   * label beside it, at caption size to sit with the rest of the popover.
   */
  const renderCheckbox = (
    label: string,
    checked: boolean,
    onChange: (next: boolean) => void
  ) => (
    <ViewSettingCheckboxLabel
      key={label}
      slotProps={{ typography: { variant: 'body1' } }}
      control={
        <Checkbox
          size="small"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
        />
      }
      label={label}
    />
  );

  // Side by side rather than stacked, so the two read as a related pair on one line.
  const checkboxes = (
    <BacklogCheckboxRow>
      {renderCheckbox(
        t('filters.showCompleted'),
        showCompleted,
        onShowCompletedChange
      )}
      {renderCheckbox(t('filters.showTotals'), showTotals, onShowTotalsChange)}
    </BacklogCheckboxRow>
  );

  // A press outside puts the popover away, but the eye itself is outside it — that press is the toggle
  // that opened it, so letting it through would close the popover in the same gesture.
  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    const anchor = document.getElementById(anchorId);
    if (anchor?.contains(event.target as Node)) return;
    onClose();
  };

  if (!isOpen) return null;

  // Across the top the popper drops down under the icon, aligned to its right edge so a popover opened
  // from the toolbar's far end stays on screen; docked to the side it opens into the page to its left,
  // matching the filters popover on the other end of the toolbar.
  return (
    <ClickAwayListener mouseEvent="onMouseUp" onClickAway={handleClickAway}>
      <Popper
        open
        anchorEl={document.getElementById(anchorId)}
        placement={isVerticalToolbar ? 'left' : 'bottom-end'}
        modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
      >
        <Paper>
          <StyledPaperBox>
            {/* No title of its own: the popover hangs directly off the eye it was opened from, which
                already says what it is, and the two settings read plainly without a heading over them. */}
            <BacklogControlStack>
              {cellValueSelect}
              {checkboxes}
            </BacklogControlStack>
          </StyledPaperBox>
        </Paper>
      </Popper>
    </ClickAwayListener>
  );
};

export default BacklogViewSettings;
